/* eslint-disable max-depth */
/* eslint-disable quote-props */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    MenuItem,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Grid,
    Switch,
    TextField
} from '@mui/material';
import { MessageQuestionCustomLinearIcon } from '../../../../../../../icons-lib';
import i18n from '../../../../../../../i18';
import { swissCities } from '../../../../../../../config/swissCities';

export const DeepLinksField = ({ field, data, onChange }) => {
    const { id, label, tipText } = field;
    const [deepLink, setDeepLink] = useState('');
    const [tagInput, setTagInput] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('city');
    const [isEnabled, setIsEnabled] = useState(false);
    const isInitialized = useRef(false);

    // Initialize with data - handle single string
    useEffect(() => {
        if (!isInitialized.current) {
            if (data && typeof data === 'string' && data.trim() !== '') {
                // Use existing deepLink string
                setDeepLink(data);
                setIsEnabled(true);

                // Determine filter type based on URL
                let filterType = 'city';
                if (data.includes('keywords=')) {
                    filterType = 'tag';
                    // Extract keywords value from URL
                    try {
                        const params = new URLSearchParams(data.split('?')[1]);
                        const keywordsValue = params.get('keywords');
                        if (keywordsValue) {
                            setTagInput(decodeURIComponent(keywordsValue));
                        }
                    } catch (e) {
                        console.error('Error parsing keywords from URL:', e);
                    }
                }
                setSelectedFilter(filterType);
            } else {
                // Initialize with empty deep link
                setDeepLink('');
                setSelectedFilter('city');
                setIsEnabled(false);
                onChange(id, null); // Send null when disabled
            }
            isInitialized.current = true;
        }
    }, [data, id, onChange]);

    // Handle switch enable/disable
    useEffect(() => {
        if (isEnabled && !deepLink) {
            // When enabled for the first time, set default checkout URL
            const defaultUrl = 'app://checkout';
            setDeepLink(defaultUrl);
            onChange(id, defaultUrl);
        }
    }, [isEnabled, deepLink, id, onChange]);

    const parseDeepLinkUrl = (url) => {
        if (!url || url === '' || url === 'app://checkout') {
            return { type: 'checkout', city: '', keywords: '' };
        } else if (url.startsWith('app://map?')) {
            const params = new URLSearchParams(url.split('?')[1]);
            const city = params.get('city') || '';
            const keywords = params.get('keywords') || '';
            return { type: 'map', city, keywords };
        } else if (url === 'app://map') {
            return { type: 'map', city: '', keywords: '' };
        }
        return { type: 'checkout', city: '', keywords: '' };
    };

    const generateDeepLinkUrl = (type, city, keywords) => {
        switch (type) {
            case 'checkout':
                return 'app://checkout';
            case 'map':
                if (city) {
                    return `app://map?city=${encodeURIComponent(city)}`;
                } else if (keywords) {
                    return `app://map?keywords=${encodeURIComponent(keywords)}`;
                }
                return 'app://map';
            default:
                return 'app://checkout';
        }
    };

    // Handle tag input change and generate URL
    const handleTagInputChange = (value) => {
        setTagInput(value);
        const newUrl = generateDeepLinkUrl('map', '', value);
        setDeepLink(newUrl);
        if (isEnabled) {
            onChange(id, newUrl);
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Box display={'flex'} alignItems={'center'} sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {i18n.t(`columns.${label}`)}
                </Typography>
                {tipText && (
                    <Tooltip
                        title={i18n.t(`tooltips.${tipText}`)}
                        placement="top"
                    >
                        <IconButton size="small">
                            <MessageQuestionCustomLinearIcon
                                sx={{ fontSize: '1rem' }}
                            />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Enable/Disable Switch */}
            <Box sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={isEnabled}
                            onChange={(e) => {
                                const enabled = e.target.checked;
                                setIsEnabled(enabled);
                                if (enabled) {
                                    // Enable deep link - set default checkout if empty
                                    const defaultUrl =
                                        deepLink || 'app://checkout';
                                    setDeepLink(defaultUrl);
                                    onChange(id, defaultUrl);
                                } else {
                                    // Disable deep link - send null
                                    onChange(id, null);
                                }
                            }}
                        />
                    }
                    label={i18n.t('campaigns.enableDeepLink')}
                />
            </Box>

            {isEnabled && (
                <Box
                    sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        backgroundColor: '#fafafa'
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl component="fieldset">
                                <FormLabel component="legend">
                                    {i18n.t('campaigns.deepLinkType')}
                                </FormLabel>
                                <RadioGroup
                                    value={parseDeepLinkUrl(deepLink).type}
                                    onChange={(e) => {
                                        const selectedType = e.target.value;
                                        let newUrl;
                                        if (selectedType === 'checkout') {
                                            newUrl = 'app://checkout';
                                        } else {
                                            // map
                                            newUrl = 'app://map';
                                        }

                                        setDeepLink(newUrl);
                                        if (isEnabled) {
                                            onChange(id, newUrl);
                                        }
                                    }}
                                    row
                                >
                                    <FormControlLabel
                                        value="checkout"
                                        control={<Radio />}
                                        label={i18n.t('campaigns.checkout')}
                                    />
                                    <FormControlLabel
                                        value="map"
                                        control={<Radio />}
                                        label={i18n.t('campaigns.map')}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        {parseDeepLinkUrl(deepLink).type === 'map' && (
                            <>
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <FormLabel>
                                            {i18n.t('campaigns.filterBy')}
                                        </FormLabel>
                                        <RadioGroup
                                            value={selectedFilter}
                                            onChange={(e) => {
                                                const newFilter =
                                                    e.target.value;
                                                setSelectedFilter(newFilter);

                                                const { city } =
                                                    parseDeepLinkUrl(deepLink);

                                                // Reset to base map URL when switching filters
                                                let newUrl;
                                                if (newFilter === 'city') {
                                                    newUrl =
                                                        generateDeepLinkUrl(
                                                            'map',
                                                            city || '',
                                                            ''
                                                        );
                                                    setTagInput('');
                                                } else {
                                                    newUrl =
                                                        generateDeepLinkUrl(
                                                            'map',
                                                            '',
                                                            tagInput || ''
                                                        );
                                                }

                                                setDeepLink(newUrl);
                                                if (isEnabled) {
                                                    onChange(id, newUrl);
                                                }
                                            }}
                                            row
                                        >
                                            <FormControlLabel
                                                value="city"
                                                control={<Radio />}
                                                label={i18n.t('campaigns.city')}
                                            />
                                            <FormControlLabel
                                                value="tag"
                                                control={<Radio />}
                                                label={i18n.t('campaigns.tag')}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                {selectedFilter === 'city' ? (
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <FormLabel>
                                                {i18n.t('campaigns.selectCity')}
                                            </FormLabel>
                                            <Select
                                                value={
                                                    parseDeepLinkUrl(deepLink)
                                                        .city || ''
                                                }
                                                onChange={(e) => {
                                                    const newUrl =
                                                        generateDeepLinkUrl(
                                                            'map',
                                                            e.target.value,
                                                            ''
                                                        );
                                                    setDeepLink(newUrl);
                                                    if (isEnabled) {
                                                        onChange(id, newUrl);
                                                    }
                                                }}
                                                displayEmpty
                                            >
                                                <MenuItem value="">
                                                    <em>
                                                        {i18n.t(
                                                            'campaigns.selectCity'
                                                        )}
                                                    </em>
                                                </MenuItem>
                                                {[...swissCities]
                                                    .sort((a, b) => {
                                                        // Sort alphabetically by name, using locale-aware comparison
                                                        return a.name.localeCompare(
                                                            b.name,
                                                            i18n.language ||
                                                                'en',
                                                            {
                                                                sensitivity:
                                                                    'base',
                                                                numeric: true
                                                            }
                                                        );
                                                    })
                                                    .map((cityOption) => (
                                                        <MenuItem
                                                            key={
                                                                cityOption.code
                                                            }
                                                            value={
                                                                cityOption.code
                                                            }
                                                        >
                                                            {cityOption.name}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ) : (
                                    <Grid item xs={6}>
                                        <FormControl fullWidth>
                                            <FormLabel>
                                                {i18n.t('campaigns.enterTag')}
                                            </FormLabel>
                                            <TextField
                                                value={tagInput}
                                                onChange={(e) =>
                                                    handleTagInputChange(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={i18n.t(
                                                    'campaigns.tagPlaceholder'
                                                )}
                                                size="small"
                                                fullWidth
                                                sx={{ mt: 1 }}
                                            />
                                        </FormControl>
                                    </Grid>
                                )}
                            </>
                        )}

                        <Grid item xs={12}>
                            <Box sx={{ mt: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {i18n.t('campaigns.generatedUrl')}:
                                </Typography>
                                <Chip
                                    label={deepLink}
                                    variant="outlined"
                                    size="small"
                                    sx={{ ml: 1 }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Box>
    );
};
